<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div id="fragcolours" class="col-lg-12 position-relative mt-n3">

        <!-- Header row -->
        <div class="ts-head d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-3">
          <div class="ts-head-left d-flex align-items-center gap-2">
            <h6 class="ts-title mb-0">Report colours</h6>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-if="!isLoading">{{ totalItems }}</span>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-else>...</span>
          </div>

          <div class="ts-head-right d-flex flex-wrap gap-2 justify-content-start justify-content-xl-end align-items-center">
            <div class="ts-search-wrap flex-grow-1 flex-md-grow-0" style="min-width: 220px;">
              <svg class="ts-search-ico" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input v-model="searchQuery" @input="debounceSearch" class="ts-search-input w-100" placeholder="Search by grade..." />
            </div>

            <button class="ts-btn-add d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0" @click="openAddModal">
              Add New
              <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="ms-1">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Info note — helps users understand why the list might be empty -->
        <p class="text-xs text-muted mb-3" v-if="!isLoading && gradeOptions.length === 0">
          No grades exist yet for this school. Add grades first in <router-link :to="{name: 'Grades'}">Grades</router-link>, then return here to configure colours.
        </p>

        <!-- List card -->
        <div class="card ts-card">
          <div class="ts-card-body">

            <!-- Empty state -->
            <div v-if="!isLoading && paginatedItems.length === 0 && gradeOptions.length > 0" class="ts-empty">
              <svg width="52" height="52" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 24 24" style="opacity:.18">
                <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
              </svg>
              <p class="ts-empty-title">No report colours configured</p>
              <p class="ts-empty-sub">Add a colour per grade to style your printed reports</p>
            </div>

            <!-- Desktop table -->
            <div v-show="isLoading || paginatedItems.length > 0" class="table-responsive d-none d-md-block">
              <table class="ts-table" data-ts-mobile="cards">
                <thead>
                <tr>
                  <th>Grade</th>
                  <th>Colour</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="isLoading">
                  <tr v-for="n in pageSize" :key="'sk'+n" class="ts-skel-row">
                    <td data-label="Grade"><div class="ts-skel" style="width:80px;height:12px"></div></td>
                    <td data-label="Colour">
                      <div class="d-flex align-items-center gap-2">
                        <div class="ts-skel" style="width:24px;height:24px;border-radius:6px"></div>
                        <div class="ts-skel" style="width:80px;height:12px"></div>
                      </div>
                    </td>
                    <td data-label="Actions"><div class="ts-skel" style="width:90px;height:28px;border-radius:8px;margin:0 auto"></div></td>
                  </tr>
                </template>

                <template v-else>
                  <tr v-for="item in paginatedItems" :key="item.id">
                    <td data-label="Grade"><strong>{{ item.gradeName || '—' }}</strong></td>
                    <td data-label="Colour">
                      <div class="d-flex align-items-center gap-2">
                        <span class="rc-swatch" :style="{ background: item.colour }"></span>
                        <span class="text-xs text-muted font-monospace">{{ item.colour }}</span>
                      </div>
                    </td>
                    <td data-label="Actions">
                      <div class="ts-actions justify-content-center">
                        <button class="ts-btn-edit" @click="editItem(item)" title="Edit">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                        </button>
                        <button class="ts-btn-suspend" @click="deleteItem(item)" title="Delete">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <!-- Mobile cards -->
            <div class="d-md-none ts-mobile-list" v-show="isLoading || paginatedItems.length > 0">
              <template v-if="isLoading">
                <div v-for="n in 3" :key="'msk'+n" class="ts-card-m ts-skel-card">
                  <div class="ts-skel" style="width:55%;height:13px;margin-bottom:7px"></div>
                  <div class="ts-skel" style="width:80%;height:10px"></div>
                  <div class="ts-skel" style="height:36px;border-radius:10px;margin-top:14px"></div>
                </div>
              </template>
              <template v-else>
                <div v-for="item in paginatedItems" :key="'m'+item.id" class="ts-card-m">
                  <div class="ts-card-top">
                    <div class="ts-card-info">
                      <div class="ts-name">{{ item.gradeName || '—' }}</div>
                      <div class="d-flex align-items-center gap-2 mt-1">
                        <span class="rc-swatch" :style="{ background: item.colour }"></span>
                        <span class="text-xs text-muted font-monospace">{{ item.colour }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="ts-card-acts">
                    <button class="ts-btn-edit-full" @click="editItem(item)">Edit</button>
                    <button class="ts-btn-suspend-full" @click="deleteItem(item)">Delete</button>
                  </div>
                </div>
              </template>
            </div>

            <!-- Pagination -->
            <div v-if="!isLoading && totalItems > 0" class="ts-pagination">
              <span class="ts-pg-info">Showing {{ paginationFrom }} to {{ paginationTo }} of {{ totalItems }}</span>
              <div class="ts-pg-btns">
                <button class="ts-pg-btn" @click="currentPage--" :disabled="currentPage === 1">
                  <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <template v-for="p in pageButtons" :key="String(p)">
                  <button v-if="p === '…'" class="ts-pg-btn" style="opacity:.35;pointer-events:none;cursor:default">…</button>
                  <button v-else class="ts-pg-btn" :class="{ 'ts-pg-active': p === currentPage }" @click="currentPage = p">{{ p }}</button>
                </template>
                <button class="ts-pg-btn" @click="currentPage++" :disabled="currentPage === lastPage">
                  <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </main>

  <!-- ═══ ADD / EDIT MODAL ═══ -->
  <div v-if="showModal" class="tw-overlay z-index-10000" @mousedown.self="closeModal">
    <div class="tw-modal" style="max-width: 440px;">
      <div class="tw-header">
        <div>
          <h5 class="tw-title">{{ isEditing ? 'Edit Colour' : 'Add Colour' }}</h5>
          <p class="tw-subtitle" v-if="isEditing && form.gradeName">{{ form.gradeName }}</p>
        </div>
        <button class="tw-close" @click="closeModal">
          <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div class="tw-body" style="background: var(--surface-2, #f8fafc);">
        <div class="card p-3 shadow-sm border-0">
          <div class="row g-3">
            <div class="col-12">
              <label class="tw-label">Grade <span class="tw-req">*</span></label>
              <!-- Choices.js enhances this select. Note the ref — used for init/destroy. -->
              <select ref="gradeSelect" v-model="form.grade" class="tw-select">
                <option :value="0" disabled>— Select a grade —</option>
                <option v-for="g in availableGradeOptions" :key="g.id" :value="g.id">{{ g.grade }}</option>
              </select>
              <p class="text-xs text-muted mb-0 mt-1" v-if="isEditing">
                Each grade can have only one colour — changing grade here moves the colour to that grade.
              </p>
            </div>

            <div class="col-12">
              <label class="tw-label">Colour <span class="tw-req">*</span></label>
              <div class="d-flex align-items-center gap-3">
                <input v-model="form.colour" type="color" class="tw-input" style="width:80px;height:42px;padding:4px;flex:0 0 auto;" />
                <input v-model="form.colour" type="text" class="tw-input" placeholder="#f6b73c" pattern="^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$" maxlength="7" />
              </div>
              <p class="text-xs text-muted mb-0 mt-1">Hex code. Either picker or text field works.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="tw-footer d-flex justify-content-end gap-2 p-3 bg-white border-top">
        <button type="button" class="tw-btn tw-btn-ghost" @click="closeModal">Cancel</button>
        <button type="button" class="tw-btn tw-btn-primary" @click="submitForm" :disabled="isSubmitting">
          {{ isSubmitting ? 'Saving...' : (isEditing ? 'Save' : 'Add') }}
        </button>
      </div>
    </div>
  </div>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import { Choices } from '@/utils/ShuleChoices';
import swal from 'sweetalert';

export default {
  name: "ReportColoursPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      // ── List state ────────────────────────────────────────────────
      items: [],
      gradeOptions: [],
      isLoading: true,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      searchTimer: null,
      resizeTimer: null,

      // ── Modal state ──────────────────────────────────────────────
      showModal: false,
      isEditing: false,
      isSubmitting: false,
      form: this.blankForm(),

      // Choices.js instance — kept on the component so beforeUnmount
      // and modal close can destroy it cleanly.
      gradeChoices: null,
    };
  },

  computed: {
    filteredItems() {
      if (!this.searchQuery) return this.items;
      const q = this.searchQuery.toLowerCase();
      return this.items.filter(i => (i.gradeName || '').toLowerCase().includes(q));
    },
    totalItems() { return this.filteredItems.length; },
    lastPage()   { return Math.ceil(this.totalItems / this.pageSize) || 1; },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    paginationFrom() { return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    paginationTo()   { return Math.min(this.currentPage * this.pageSize, this.totalItems); },
    pageButtons() {
      const total = this.lastPage, cur = this.currentPage, pages = [];
      for (let p = 1; p <= total; p++) {
        if (total > 7 && p > 2 && p < total - 1 && Math.abs(p - cur) > 1) {
          if (p === 3 || p === total - 2) pages.push('…');
          continue;
        }
        pages.push(p);
      }
      return pages;
    },

    /**
     * When adding a new colour, exclude grades that already have one —
     * saves the user a trip to a 409 error message. On edit, keep the
     * current grade in the list (otherwise it vanishes from its own select).
     */
    availableGradeOptions() {
      const takenIds = new Set(
          this.items
              .filter(i => !this.isEditing || i.id !== this.form.id)
              .map(i => i.grade)
      );
      return this.gradeOptions.filter(g => !takenIds.has(g.id));
    },
  },

  mounted() {
    this.calculatePageSize();
    window.addEventListener('resize', this.onResize);
    this.fetchAll();

    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    this.destroyGradeChoices();
  },

  methods: {
    // ═════════════════════════════════════════════════════════════════
    // List helpers
    // ═════════════════════════════════════════════════════════════════

    calculatePageSize() {
      const isMobile = window.innerWidth < 768;
      const headerH = 260, footerH = 60, pagH = 60;
      const available = window.innerHeight - (headerH + footerH + pagH);
      const itemH = isMobile ? 120 : 56;
      this.pageSize = Math.max(Math.floor(available / itemH), 5);
    },

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.calculatePageSize();
        this.currentPage = 1;
      }, 150);
    },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.currentPage = 1; }, 300);
    },

    /**
     * Fetch colours AND grade options in parallel. Grade options are
     * needed both for the modal select and for the empty-state hint
     * ("add grades first"), so we wait for both before clearing loading.
     */
    async fetchAll() {
      this.isLoading = true;
      try {
        const [coloursRes, gradesRes] = await Promise.all([
          apiService.apiClient.get('/api/academics/report-colours'),
          apiService.apiClient.get('/api/academics/report-colours/grade-options'),
        ]);
        this.items = Array.isArray(coloursRes.data) ? coloursRes.data : [];
        this.gradeOptions = Array.isArray(gradesRes.data) ? gradesRes.data : [];
      } catch (e) {
        console.error(e);
        this.items = [];
        this.gradeOptions = [];
        swal("Error", this.readErr(e, "Could not load report colours"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    // ═════════════════════════════════════════════════════════════════
    // Modal open/close
    // ═════════════════════════════════════════════════════════════════

    openAddModal() {
      if (this.gradeOptions.length === 0) {
        swal("No grades", "Add grades first, then configure colours for each.", "info");
        return;
      }
      if (this.availableGradeOptions.length === 0 && this.items.length > 0) {
        swal("All grades configured", "Every grade already has a colour. Edit an existing row instead.", "info");
        return;
      }
      this.isEditing = false;
      this.form = this.blankForm();
      this.showModal = true;
      this.$nextTick(() => this.initGradeChoices());
    },

    async editItem(item) {
      // The list row has grade + gradeName + colour — enough to open the
      // modal without a round-trip. A GET /{id} would also work but the
      // list already has everything we need.
      this.form = {
        id: item.id,
        grade: item.grade,
        gradeName: item.gradeName,
        colour: item.colour,
      };
      this.isEditing = true;
      this.showModal = true;
      this.$nextTick(() => this.initGradeChoices());
    },

    closeModal() {
      this.destroyGradeChoices();
      this.showModal = false;
    },

    // ═════════════════════════════════════════════════════════════════
    // Choices.js lifecycle
    // ═════════════════════════════════════════════════════════════════

    initGradeChoices() {
      this.destroyGradeChoices();

      if (!this.$refs.gradeSelect) return;
      this.gradeChoices = new Choices(this.$refs.gradeSelect, {
        searchEnabled: true,
        shouldSort: false,
        duplicateItemsAllowed: false,
      });

      // Build the option set — on edit, include the current grade so the
      // display shows it; on create, exclude taken grades via availableGradeOptions.
      const options = [
        { value: '0', label: '— Select a grade —', disabled: true, selected: !this.form.grade },
      ];
      this.availableGradeOptions.forEach(g => {
        options.push({
          value: String(g.id),
          label: g.grade,
          selected: this.form.grade === g.id,
        });
      });
      // On edit, the current grade might already be excluded by
      // availableGradeOptions — add it back so it's visible.
      if (this.isEditing && this.form.grade && !this.availableGradeOptions.find(g => g.id === this.form.grade)) {
        options.push({
          value: String(this.form.grade),
          label: this.form.gradeName || `Grade ${this.form.grade}`,
          selected: true,
        });
      }

      this.gradeChoices.setChoices(options, 'value', 'label', true);
      if (this.form.grade) {
        this.gradeChoices.setChoiceByValue(String(this.form.grade));
      }

      this.$refs.gradeSelect.addEventListener('change', (e) => {
        this.form.grade = Number(e.target.value) || 0;
      });
    },

    destroyGradeChoices() {
      try { if (this.gradeChoices) this.gradeChoices.destroy(); } catch (e) {}
      this.gradeChoices = null;
    },

    // ═════════════════════════════════════════════════════════════════
    // Submit / delete
    // ═════════════════════════════════════════════════════════════════

    async submitForm() {
      if (!this.form.grade) {
        swal("Required", "Select a grade", "warning");
        return;
      }
      // The backend's @Pattern will also check this, but surface earlier
      // to avoid a round-trip just to learn the colour is malformed.
      if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(this.form.colour || '')) {
        swal("Invalid colour", "Please enter a hex code like #f6b73c", "warning");
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = {
          grade: this.form.grade,
          colour: this.form.colour.toLowerCase(),
        };
        if (this.isEditing) {
          await apiService.apiClient.put(`/api/academics/report-colours/${this.form.id}`, payload);
          swal("Saved", "Colour updated", "success");
        } else {
          await apiService.apiClient.post('/api/academics/report-colours', payload);
          swal("Created", "Colour added", "success");
        }
        this.closeModal();
        this.fetchAll();
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save colour"), "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteItem(item) {
      const ok = await swal({
        title: "Remove this colour?",
        text: `The colour for "${item.gradeName}" will be removed from reports.`,
        icon: "warning",
        buttons: ["Cancel", "Remove"],
        dangerMode: true,
      });
      if (!ok) return;

      try {
        await apiService.apiClient.delete(`/api/academics/report-colours/${item.id}`);
        swal("Removed", "Colour has been removed", "success");
        this.fetchAll();
      } catch (e) {
        swal("Error", this.readErr(e, "Could not remove colour"), "error");
      }
    },

    // ═════════════════════════════════════════════════════════════════

    blankForm() {
      return { id: null, grade: 0, gradeName: '', colour: '#f6b73c' };
    },

    readErr(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.querystatus) || fallback;
    },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>

<style scoped>
.rc-swatch {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
</style>